/**
 * KeyboardShortcuts - Sistema de atajos de teclado
 * Incluye panel de referencia y manejo global de teclas
 */
import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { 
  Keyboard, X, MagnifyingGlass, ChartLine, ArrowsOut, 
  Command, CaretUp, CaretDown, House, Gear
} from '@phosphor-icons/react';

// Definición de atajos disponibles
export const SHORTCUTS = [
  { 
    category: 'Navegación',
    shortcuts: [
      { keys: ['Ctrl', 'K'], action: 'Búsqueda rápida', id: 'search' },
      { keys: ['Ctrl', 'H'], action: 'Ir al inicio', id: 'home' },
      { keys: ['Escape'], action: 'Cerrar ventana/modal', id: 'close' },
    ]
  },
  {
    category: 'Gráfico',
    shortcuts: [
      { keys: ['V'], action: 'Cambiar a Velas', id: 'candlestick' },
      { keys: ['L'], action: 'Cambiar a Línea', id: 'line' },
      { keys: ['F'], action: 'Modo enfocado', id: 'focus' },
      { keys: ['C'], action: 'Capturar gráfico', id: 'capture' },
    ]
  },
  {
    category: 'Períodos',
    shortcuts: [
      { keys: ['1'], action: 'Período 1 día', id: 'period1d' },
      { keys: ['2'], action: 'Período 1 semana', id: 'period1w' },
      { keys: ['3'], action: 'Período 1 mes', id: 'period1m' },
      { keys: ['4'], action: 'Período 3 meses', id: 'period3m' },
      { keys: ['5'], action: 'Período 1 año', id: 'period1y' },
    ]
  },
  {
    category: 'Herramientas',
    shortcuts: [
      { keys: ['Ctrl', 'I'], action: 'Abrir Análisis Técnico Profesional', id: 'indicadoresPro' },
      { keys: ['Ctrl', 'S'], action: 'Abrir Señales', id: 'signals' },
      { keys: ['Ctrl', 'P'], action: 'Abrir Portafolio', id: 'portfolio' },
      { keys: ['Ctrl', 'N'], action: 'Abrir Noticias', id: 'news' },
    ]
  }
];

// Hook para manejar atajos de teclado
export const useKeyboardShortcuts = ({
  onSearch,
  onHome,
  onClose,
  onChartType,
  onFocus,
  onCapture,
  onPeriodChange,
  onOpenWindow,
  enabled = true
}) => {
  const [showShortcutsPanel, setShowShortcutsPanel] = useState(false);

  const handleKeyDown = useCallback((e) => {
    if (!enabled) return;
    
    // Ignorar si está escribiendo en un input
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
      // Solo permitir Escape en inputs
      if (e.key === 'Escape') {
        e.target.blur();
      }
      return;
    }

    const key = e.key.toLowerCase();
    const isCtrl = e.ctrlKey || e.metaKey;

    // Ctrl + K: Búsqueda
    if (isCtrl && key === 'k') {
      e.preventDefault();
      onSearch?.();
      return;
    }

    // Ctrl + H: Inicio
    if (isCtrl && key === 'h') {
      e.preventDefault();
      onHome?.();
      return;
    }

    // Escape: Cerrar
    if (key === 'escape') {
      e.preventDefault();
      if (showShortcutsPanel) {
        setShowShortcutsPanel(false);
      } else {
        onClose?.();
      }
      return;
    }

    // ? : Mostrar atajos
    if (key === '?' || (isCtrl && key === '/')) {
      e.preventDefault();
      setShowShortcutsPanel(prev => !prev);
      return;
    }

    // V: Velas
    if (key === 'v' && !isCtrl) {
      e.preventDefault();
      onChartType?.('candlestick');
      return;
    }

    // L: Línea
    if (key === 'l' && !isCtrl) {
      e.preventDefault();
      onChartType?.('line');
      return;
    }

    // F: Modo enfocado
    if (key === 'f' && !isCtrl) {
      e.preventDefault();
      onFocus?.();
      return;
    }

    // C: Capturar
    if (key === 'c' && !isCtrl) {
      e.preventDefault();
      onCapture?.();
      return;
    }

    // 1-5: Períodos
    if (['1', '2', '3', '4', '5'].includes(key) && !isCtrl) {
      e.preventDefault();
      const periods = ['1d', '1wk', '1mo', '3mo', '1y'];
      onPeriodChange?.(periods[parseInt(key) - 1]);
      return;
    }

    // Ctrl + I: Análisis Técnico Profesional
    if (isCtrl && key === 'i') {
      e.preventDefault();
      onOpenWindow?.('indicadoresPro');
      return;
    }

    // Ctrl + S: Señales (prevenir guardar página)
    if (isCtrl && key === 's') {
      e.preventDefault();
      onOpenWindow?.('signals');
      return;
    }

    // Ctrl + P: Portafolio (prevenir imprimir)
    if (isCtrl && key === 'p') {
      e.preventDefault();
      onOpenWindow?.('portfolio');
      return;
    }

    // Ctrl + N: Noticias
    if (isCtrl && key === 'n') {
      e.preventDefault();
      onOpenWindow?.('news');
      return;
    }
  }, [enabled, onSearch, onHome, onClose, onChartType, onFocus, onCapture, onPeriodChange, onOpenWindow, showShortcutsPanel]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return { showShortcutsPanel, setShowShortcutsPanel };
};

// Componente del panel de atajos
const KeyboardShortcutsPanel = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return createPortal(
    <>
      <div 
        className="fixed inset-0 flex items-center justify-center p-4"
        style={{ zIndex: 100000 }}
      >
        <div 
          className="w-full max-w-2xl rounded-2xl overflow-hidden"
          style={{
            background: 'linear-gradient(180deg, rgba(15, 28, 50, 0.99) 0%, rgba(10, 20, 38, 1) 100%)',
            border: '2px solid rgba(0, 220, 255, 0.3)',
            boxShadow: '0 25px 60px rgba(0, 0, 0, 0.8), 0 0 40px rgba(0, 220, 255, 0.2)'
          }}
        >
          {/* Header */}
          <div 
            className="h-[2px]"
            style={{ background: 'linear-gradient(90deg, transparent, #00dcff, #00ff6a, #00dcff, transparent)' }}
          />
          
          <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid rgba(0, 220, 255, 0.15)' }}>
            <div className="flex items-center gap-3">
              <div 
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{
                  background: 'linear-gradient(135deg, rgba(0, 220, 255, 0.2) 0%, rgba(0, 220, 255, 0.05) 100%)',
                  border: '1px solid rgba(0, 220, 255, 0.3)'
                }}
              >
                <Keyboard size={20} className="text-gold-400" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Atajos de Teclado</h2>
                <p className="text-xs text-gray-500">Presiona ? para abrir/cerrar este panel</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-white/10 text-gray-500 hover:text-white transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {/* Contenido */}
          <div className="p-6 grid grid-cols-2 gap-6 max-h-[60vh] overflow-y-auto">
            {SHORTCUTS.map((category) => (
              <div key={category.category}>
                <h3 className="text-sm font-bold text-gold-400 uppercase tracking-wider mb-3">
                  {category.category}
                </h3>
                <div className="space-y-2">
                  {category.shortcuts.map((shortcut) => (
                    <div 
                      key={shortcut.id}
                      className="flex items-center justify-between py-2 px-3 rounded-lg"
                      style={{ background: 'rgba(255, 255, 255, 0.03)' }}
                    >
                      <span className="text-sm text-gray-300">{shortcut.action}</span>
                      <div className="flex items-center gap-1">
                        {shortcut.keys.map((key, idx) => (
                          <React.Fragment key={key}>
                            {idx > 0 && <span className="text-gray-600 text-xs">+</span>}
                            <kbd 
                              className="px-2 py-1 rounded text-xs font-mono font-bold"
                              style={{
                                background: 'linear-gradient(180deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                                border: '1px solid rgba(255,255,255,0.2)',
                                boxShadow: '0 2px 0 rgba(0,0,0,0.3)',
                                color: '#fff'
                              }}
                            >
                              {key}
                            </kbd>
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div 
            className="px-6 py-3 text-center"
            style={{ borderTop: '1px solid rgba(0, 220, 255, 0.1)', background: 'rgba(0, 0, 0, 0.2)' }}
          >
            <span className="text-xs text-gray-500">
              Presiona <kbd className="px-1.5 py-0.5 rounded bg-white/10 text-white font-mono">Escape</kbd> para cerrar
            </span>
          </div>
        </div>
      </div>
      
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm"
        style={{ zIndex: 99999 }}
        onClick={onClose}
      />
    </>,
    document.body
  );
};

// Botón para mostrar atajos (para el sidebar)
export const ShortcutsButton = ({ onClick }) => {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg transition-all duration-200 hover:bg-gold-500/10"
      style={{ border: '1px solid transparent' }}
      title="Ver atajos de teclado (?)"
    >
      <div 
        className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
        style={{
          background: 'linear-gradient(135deg, rgba(0, 220, 255, 0.25) 0%, rgba(0, 220, 255, 0.1) 100%)',
          border: '1px solid rgba(0, 220, 255, 0.35)'
        }}
      >
        <Keyboard size={16} className="text-gold-400" />
      </div>
      <div className="flex-1 text-left min-w-0">
        <div className="text-xs font-semibold text-white/85">Atajos de Teclado</div>
        <div className="text-[10px] text-gray-500">Presiona ? para ver</div>
      </div>
    </button>
  );
};

export default KeyboardShortcutsPanel;
